import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { toCurrency } from "./shopUtils";

export default function ShopCheckoutDialog({
  open,
  onClose,
  cartItems,
  cartTotal,
  contractOwner,
  checkoutMemberName,
  onConfirm,
  creatingOrder,
  hasContractContext,
  isAdminCheckout,
  fulfillmentType,
  onFulfillmentTypeChange,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Bestellübersicht</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            Bitte prüfe alle Positionen. Erst mit Klick auf{" "}
            <strong>KOSTENPFLICHTIG BESTELLEN</strong> wird die Bestellung verbindlich
            angelegt.
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              background:
                "linear-gradient(140deg, rgba(14,165,233,0.08) 0%, rgba(16,185,129,0.08) 100%)",
            }}
          >
            {checkoutMemberName ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Mitglied
                </Typography>
                <Typography variant="subtitle2">
                  {checkoutMemberName}
                </Typography>
              </>
            ) : null}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={checkoutMemberName ? { mt: 1 } : undefined}
            >
              {checkoutMemberName ? "Abrechnung" : "Vertrag"}
            </Typography>
            <Typography variant="subtitle2">
              {contractOwner || "Unbekannter Vertrag"}
            </Typography>
          </Paper>

          {isAdminCheckout && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Versandart
              </Typography>
              <ToggleButtonGroup
                value={fulfillmentType || "shipping"}
                exclusive
                onChange={(_, value) => {
                  if (value) onFulfillmentTypeChange?.(value);
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="shipping">
                  <Iconify icon="mdi:truck-delivery-outline" width={18} sx={{ mr: 0.75 }} />
                  Versand
                </ToggleButton>
                <ToggleButton value="local">
                  <Iconify icon="mdi:hand-extended-outline" width={18} sx={{ mr: 0.75 }} />
                  Vor Ort ausgeben
                </ToggleButton>
              </ToggleButtonGroup>
              {fulfillmentType === "local" && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                  Die Bestellung wird sofort als &quot;Ausgegeben&quot; markiert. Kein Versand-Webhook.
                </Typography>
              )}
            </Paper>
          )}

          <Stack spacing={1}>
            {cartItems.map((item) => (
              <Paper
                key={`checkout-${item.productId}-${item.variantKey}`}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 2 }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box>
                    <Typography variant="subtitle2">{item.productName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.variantLabel !== "Standard"
                        ? `${item.variantLabel} · `
                        : ""}
                      Menge {item.quantity}
                    </Typography>
                    {Number(item.quantity) > 1 ? (
                      <Typography variant="caption" color="text.disabled">
                        {toCurrency(item.unitPrice)} &times; {item.quantity}
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                    {toCurrency(Number(item.unitPrice) * Number(item.quantity))}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              Gesamt
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight={700}>
              {toCurrency(cartTotal)}
            </Typography>
          </Stack>
          {cartItems.some((item) => Number(item.taxRate || 0) > 0) ? (
            <Typography variant="caption" color="text.secondary">
              Alle Preise inkl. MwSt.
            </Typography>
          ) : null}

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "grey.50" }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Was passiert als Nächstes?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nach deiner Bestellung wird das Team informiert und die Zahlung per
              SEPA-Lastschrift eingezogen. Du erhältst eine Bestätigung, sobald die
              Bestellung verarbeitet wurde.
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Zurück
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={creatingOrder || !cartItems.length || !hasContractContext}
          fullWidth
          size="large"
          sx={{ fontWeight: 700, py: 1.25, borderRadius: 1.5 }}
        >
          KOSTENPFLICHTIG BESTELLEN
        </Button>
      </DialogActions>
    </Dialog>
  );
}
